import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, Clock, User, Tag, ExternalLink, BookOpen } from 'lucide-react';
import { getHeroImage } from '../utils/heroImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOP_URL = 'https://shop.paramountpeptides.com/MARKBECKER';
const REF_KEY_PREFIX_LENGTH = 30;

const renderParagraph = (paragraph, idx) => {
  const key = `p-${idx}-${paragraph.slice(0, 20).replace(/\s/g, '')}`;
  if (paragraph.startsWith('## ')) {
    return <h2 key={key} className="text-2xl font-bold mt-8 mb-4 text-gray-900">{paragraph.replace('## ', '')}</h2>;
  } else if (paragraph.startsWith('# ')) {
    return <h1 key={key} className="text-3xl font-bold mt-8 mb-4 text-gray-900">{paragraph.replace('# ', '')}</h1>;
  } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
    return <p key={key} className="text-lg font-semibold my-4 text-gray-800">{paragraph.replace(/\*\*/g, '')}</p>;
  } else if (paragraph.match(/^\d+\./)) {
    return <p key={key} className="text-gray-700 leading-relaxed my-3 pl-4">{paragraph}</p>;
  } else if (paragraph.startsWith('- ')) {
    return <p key={key} className="text-gray-700 leading-relaxed my-2 pl-6">{paragraph}</p>;
  }
  return <p key={key} className="text-gray-700 leading-relaxed my-4">{paragraph}</p>;
};

const ArticleReferences = ({ references }) => {
  if (!references || references.length === 0) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-testid="article-references">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen size={24} className="text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900">References & Citations</h2>
      </div>
      <ol className="space-y-3">
        {references.map((ref, idx) => (
          <li key={`ref-${ref.slice(0, REF_KEY_PREFIX_LENGTH).replace(/\s/g, '-')}`} className="text-sm text-gray-600 leading-relaxed pl-2 border-l-2 border-blue-200 ml-2">
            <span className="font-medium text-gray-800">[{idx + 1}]</span> {ref}
          </li>
        ))}
      </ol>
    </div>
  );
};

const ArticleCTA = () => (
  <div className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white rounded-xl shadow-lg p-8 mb-12 relative overflow-hidden">
    <div className="absolute inset-0 opacity-15" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
    <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
    <h3 className="text-2xl font-bold mb-4 relative z-10">Ready to Try Premium Peptides?</h3>
    <p className="mb-6 text-gray-300 relative z-10">Explore our curated selection of research-grade peptides from trusted sources.</p>
    <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all relative z-10" data-testid="article-shop-cta">
      Visit Our Shop
      <ExternalLink className="ml-2" size={18} />
    </a>
  </div>
);

const RelatedArticles = ({ articles }) => {
  if (articles.length === 0) return null;
  return (
    <div>
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Related Research</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((related) => (
          <Link key={related.id} to={`/article/${related.slug || related.id}`} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6" data-testid={`related-article-${related.slug || related.id}`}>
            <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2">{related.title}</h3>
            <p className="text-gray-600 text-sm line-clamp-2 mb-3">{related.excerpt}</p>
            <div className="text-sm text-gray-500">{related.author}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const ArticlePage = () => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadArticle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${articleSlug}`);
      const data = response.data;

      // If a numeric ID was used and the article has a slug, redirect to the slug URL
      if (/^\d+$/.test(articleSlug) && data.slug && data.slug !== articleSlug) {
        navigate(`/article/${data.slug}`, { replace: true });
        return;
      }

      setArticle(data);

      const relatedRes = await axios.get(`${API}/articles?category=${data.category}&limit=4`);
      const filtered = relatedRes.data.filter(a => a.slug !== articleSlug && a.id !== articleSlug);
      setRelatedArticles(filtered.slice(0, 3));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  }, [articleSlug, navigate]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="text-xl text-gray-600">Loading article...</div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="text-xl text-gray-600">Article not found</div>
          <Link to="/research" className="text-blue-600 hover:underline mt-4 inline-block">Return to Research Library</Link>
        </div>
      </div>
    );
  }

  const articleUrl = `https://www.peptidesciences.ai/article/${article.slug || article.id}`;
  const heroImage = getHeroImage(article.category);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": heroImage,
    "author": { "@type": "Person", "name": article.author },
    "datePublished": article.published_date,
    "keywords": article.tags ? article.tags.join(", ") : "",
    "publisher": {
      "@type": "Organization",
      "name": "PeptideSciences.ai",
      "url": "https://www.peptidesciences.ai",
      "logo": { "@type": "ImageObject", "url": "https://www.peptidesciences.ai/logo.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${article.title} | PeptideSciences.ai`}</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={articleUrl} />
        <meta name="keywords" content={article.tags ? article.tags.join(", ") : ""} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={heroImage} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Navigation />

      <article className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/research" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8" data-testid="back-to-research">
          <ArrowLeft className="mr-2" size={20} />
          Back to Research
        </Link>

        <div className="rounded-xl overflow-hidden mb-8 shadow-lg" data-testid="article-hero-image">
          <img src={heroImage} alt={article.title} className="w-full h-64 md:h-80 object-cover" loading="eager" />
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Link to={`/research/${article.category}`} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full uppercase hover:bg-blue-200 transition-colors" data-testid="article-category-link">
              {article.category.replace(/-/g, ' ')}
            </Link>
            {article.featured && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">Featured</span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 leading-tight" data-testid="article-title">{article.title}</h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-testid="article-excerpt">{article.excerpt}</p>

          <div className="flex flex-wrap items-center gap-6 text-gray-600">
            <div className="flex items-center">
              <User size={18} className="mr-2" />
              <span data-testid="article-author">{article.author}</span>
            </div>
            <div className="flex items-center">
              <Clock size={18} className="mr-2" />
              <span>{article.read_time} min read</span>
            </div>
            <span>{new Date(article.published_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>

          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              <Tag size={18} className="text-gray-500" />
              {article.tags.map((tag) => (
                <span key={`tag-${tag}`} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="prose prose-lg max-w-none" data-testid="article-content">
            {article.content.split('\n\n').map(renderParagraph)}
          </div>
        </div>

        <ArticleReferences references={article.references} />
        <ArticleCTA />
        <RelatedArticles articles={relatedArticles} />
      </article>

      <Footer />
    </div>
  );
};

export default ArticlePage;
